import * as React from 'react';
import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { translate } from './Translate';
import Icon from './Icon';
import Badge from './Badge';
import { IdentifierSearch } from '../models/configuration/IdentifierSearch';
import { AccessibilityStore, TranslationStore } from '../stores';
import { computed } from 'mobx';
import VisuallyHiddenWrapper from './VisuallyHiddenWrapper';

const SEARCH_INPUT_ID = 'shareClassSearch';

const SearchWrapper = styled<any, any>('div')`
    ${propStyles};
`;
Badge.rightAlignStyleProps = {
    order: 1,
};
Icon.rightAlignStyleProps = {
    order: 2,
};
SearchWrapper.defaultProps = {
    flex: '1 0 auto',
    display: 'flex',
    order: ['1', '0'],
    alignItems: 'center',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    paddingX: 'sm',
    paddingY: 'xs',
    minHeight: '40px',
};

const SearchInput = styled<any, any>('input')`
    ${propStyles};
    &::-ms-clear {
        display: none;
    }
`;

SearchInput.defaultProps = {
    flex: '1 0 auto',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    padding: 'sm',
    borderStyle: 'none',
    bgColor: 'inherit',
    outline: 'none',
};

const SearchLabel = styled<any, any>('label')`
    ${propStyles};
`;

SearchLabel.defaultProps = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontFamily: 'heading',
    fontWeight: 'medium',
    color: 'body',
};

interface SearchProps {
    theme?: any;
    translationStore?: TranslationStore;
    accessibilityStore?: AccessibilityStore;
    total: number;
    search: IdentifierSearch;
    onChange: (e) => void;
    clearSearch: () => void;
}

@inject('translationStore', 'accessibilityStore')
@observer
class Search extends Component<SearchProps> {
    @computed
    get searchLabel(): React.ReactNode {
        const { accessibilityStore, theme, translationStore } = this.props;
        if (!accessibilityStore) {
            return null;
        }
        const searchLabelText = translate(
            translationStore,
            accessibilityStore.searchLabel
        );

        const searchLabelComponent = (
            <SearchLabel htmlFor={SEARCH_INPUT_ID} {...theme.SearchLabel}>
                {searchLabelText || this.searchPlaceholder}
            </SearchLabel>
        );

        if (accessibilityStore.showSearchLabel) {
            return searchLabelComponent;
        } else {
            return (
                <VisuallyHiddenWrapper>
                    {searchLabelComponent}
                </VisuallyHiddenWrapper>
            );
        }
    }
    @computed
    get searchPlaceholder(): string {
        const { translationStore, total, search } = this.props;
        return translate(
            translationStore,
            total === 1
                ? search.placeholder.format.singular
                : search.placeholder.format.plural,
            {
                total,
                properties: search.properties.join(', '),
            }
        );
    }
    render() {
        const {
            theme,
            translationStore,
            total,
            onChange,
            clearSearch,
            search,
        } = this.props;
        return (
            <SearchWrapper {...theme.SearchWrapper}>
                {this.searchLabel}
                <Icon
                    fileName="SearchIcon"
                    height="1.6em"
                    width="1.6em"
                    {...theme.SearchIcon}
                />
                {search.placeholder.badge && search.placeholder.badge.show && (
                    <Badge
                        marginX="sm"
                        {...(search.placeholder.badge.align !== 'left' &&
                            Badge.rightAlignStyleProps)}
                        {...theme.SearchBadge}
                    >
                        {translate(
                            translationStore,
                            total === 1
                                ? search.placeholder.badge.format.singular
                                : search.placeholder.badge.format.plural,
                            {
                                total,
                                properties: search.properties.join(', '),
                            }
                        )}
                    </Badge>
                )}
                <SearchInput
                    id={SEARCH_INPUT_ID}
                    type="search"
                    value={search.value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={this.searchPlaceholder}
                    autoCapitalize="off"
                    autoComplete="off"
                    spellCheck="false"
                    autoCorrect="off"
                    data-metric-id={'entitySearch'}
                    {...theme.SearchInput}
                />
                {search.value && (
                    <Icon
                        fileName="CloseIcon"
                        title={translate(translationStore, 'Clear Search')}
                        onClick={clearSearch}
                        cursor="pointer"
                        height="1.6em"
                        width="1.6em"
                        data-metric-id={'entitySearch-close'}
                        {...theme.SearchCloseIcon}
                        {...Icon.rightAlignStyleProps}
                    />
                )}
            </SearchWrapper>
        );
    }
}

export default withTheme(Search);



// WEBPACK FOOTER //
// ./src/Components/Search.tsx